import React, { useMemo, useState } from 'react';
import { BsCaretDownFill, BsCaretRightFill, BsShield, BsShieldCheck, BsExclamationTriangle, BsClock, BsFiles } from "react-icons/bs";

const VaccinationKPI = ({ vaccinations = [], filteredVaccinations = [], showKPIs = true }) => {
  const [showKPI, setShowKPI] = useState(showKPIs);

  // Ensure filteredVaccinations is used if it exists, otherwise fall back to all vaccinations
  const effectiveVaccinations = useMemo(() => {
    // If filteredVaccinations is provided and different from all vaccinations, use it
    if (filteredVaccinations && filteredVaccinations.length > 0 && 
        filteredVaccinations.length !== vaccinations.length) {
      return filteredVaccinations;
    }
    // Otherwise use the full dataset
    return vaccinations;
  }, [vaccinations, filteredVaccinations]);

  // Calculate all KPIs based on current effective vaccinations
  const kpis = useMemo(() => {
    // No data case
    if (!effectiveVaccinations || effectiveVaccinations.length === 0) {
      return {
        totalVaccinations: 0,
        uniqueVaccines: 0,
        missVaccinations: 0,
        upcoming: 0,
        duplicateEntries: 0
      };
    }

    // Count unique entities and calculate metrics
    const uniqueVaccineNames = new Set();
    const uniquePatients = new Set();
    const seenCombinations = new Set();
    let missVaccinations = 0;
    let upcoming = 0;
    let duplicateEntries = 0;

    effectiveVaccinations.forEach(vaccination => {
      // Add unique vaccine names to set (using API property names)
      if (vaccination.vaccineName) {
        uniqueVaccineNames.add(vaccination.vaccineName);
      }

      // Track unique patients (using API property names)
      if (vaccination.patientId) {
        uniquePatients.add(vaccination.patientId);
      }

      // Check for missed vaccinations based on Status or Next Due Date
      const status = vaccination.status || '';
      const nextDueDate = vaccination.nextDueDate || '';
      
      // Count missed if status contains "Missed" or if next due date is in the past
      if (status.toLowerCase().includes('missed')) {
        missVaccinations++;
      } else if (nextDueDate) {
        // Check if next due date is in the past (overdue)
        const today = new Date();
        const dueDate = new Date(nextDueDate);
        if (dueDate < today && status.toLowerCase() !== 'administered') {
          missVaccinations++;
        }
      }

      // Count upcoming vaccinations
      if (status.toLowerCase().includes('scheduled') || 
          status.toLowerCase().includes('upcoming')) {
        upcoming++;
      } else if (nextDueDate && status.toLowerCase() !== 'administered') {
        // Check if next due date is in the future
        const today = new Date();
        const dueDate = new Date(nextDueDate);
        if (dueDate >= today) {
          upcoming++;
        }
      }

      // Check for duplicates based on Patient Id + Vaccine Name combination
      const combination = `${vaccination.patientId}-${vaccination.vaccineName}`;
      if (seenCombinations.has(combination)) {
        duplicateEntries++;
      } else {
        seenCombinations.add(combination);
      }
    });

    return {
      totalVaccinations: effectiveVaccinations.length,
      uniqueVaccines: uniqueVaccineNames.size,
      missVaccinations,
      upcoming,
      duplicateEntries
    };
  }, [effectiveVaccinations]);

  // Format number with commas for thousands
  const formatNumber = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return value;
  };

  // Toggle KPI visibility
  const toggleKPI = () => setShowKPI(prev => !prev);

  // Determine if filters are active
  const isFiltered = filteredVaccinations && 
                    vaccinations && 
                    filteredVaccinations.length !== vaccinations.length &&
                    filteredVaccinations.length > 0;

  // Define the KPI cards data
  const kpiCards = [
    { 
      title: 'Total', 
      value: kpis.totalVaccinations,
      icon: <BsShield />, 
      iconColor: '#f5f2e8'
    },
    { 
      title: 'Unique Vaccine', 
      value: kpis.uniqueVaccines,
      icon: <BsShieldCheck />, 
      iconColor: '#e8f4fd'
    },
    { 
      title: 'Miss Vaccinations', 
      value: kpis.missVaccinations,
      icon: <BsExclamationTriangle />, 
      iconColor: '#fde8f4'
    },
    { 
      title: 'Upcoming', 
      value: kpis.upcoming,
      icon: <BsClock />, 
      iconColor: '#e8f5e8'
    },
    { 
      title: 'Duplicate Entries', 
      value: kpis.duplicateEntries,
      icon: <BsFiles />, 
      iconColor: '#f0e8fd'
    }
  ];

  return (
    <>
      {/* <div className="d-flex align-items-center justify-content-between mb-3 mt-4">
        <div className="d-flex align-items-center">
          <h5 className="mb-0 me-3">Dashboard / Vaccination</h5>
          <button onClick={toggleKPI} className="btn text-primary p-0">
            {showKPI ? 'Hide KPI ' : 'Show KPI '}
            {showKPI ? <BsCaretDownFill size={12} /> : <BsCaretRightFill size={12} />}
          </button>
        </div>
        {isFiltered && (
          <div className="badge bg-info text-white">
            Showing filtered data ({filteredVaccinations.length} of {vaccinations.length})
          </div>
        )}
      </div> */}
      
      {showKPI && (
        <div className="row gy-3 introbx_row">
          {kpiCards.map((kpi, index) => (
            <div key={index} className="col">
              <div className="d-flex align-items-center gap-2 p-3 rounded-10 shadow-sm">
                <div className="icon flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle" style={{ width: '2rem', height: '2rem', backgroundColor: '#E4E4FF', color: '#8381FF' }}>
                  <span className="d-block material-symbols-outlined fs-lg">{kpi.icon}</span>
                </div>
                <div className="info flex-fill">
                  <p className="fs-sm text-gray mb-0">{kpi.title}</p>
                  <p className="lh-1 fw-semibold">{formatNumber(kpi.value)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default VaccinationKPI;